/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * This file contains the component that provides context for the online patient
 * management system.
 * ---------------------------------------------------------------------------------
 */

/*
 * ----------------------------------------------------------------------------------
 * Imports - External
 * ----------------------------------------------------------------------------------
 */
import { AlertTitle, Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Grid2, Paper, styled, TextField, Typography } from '@mui/material';
import { GridColDef, GridEventListener } from '@mui/x-data-grid';
import { IFormSubmit, IFormValidate, ValidateOn } from '@ngt/forms-core';
import {
    ALL_INSTITUTIONS_CODE,

    AuthenticatedUserContext,

    CollapsibleTable,
    CrfFormByCodesProvider,
    Form,
    FormSubmitButton,
    GroupedField,
    IFormValidationError,
    InstitutionBreadcrumbs,
    InstitutionsContext,
    OnlinePatientManagementContext,
    PageLayout,
    PatientsByCodesProvider,
    PatientsContext,
    RouteLoading,
    Select,
    TreatmentsContext,
    TrialContextSelector,
    useFormByCodes,
    useFormLookups,
    useFormMetadata,
    useLookups,
    useSnackbar,
    ValidationErrorType
} from '@ngt/opms';
import { Permission, usePermissionsByIds } from '@ngt/opms-bctapi';
import { DateTime } from 'luxon';
import { ChangeEventHandler, FC, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSWRConfig } from 'swr';

/*
 * ----------------------------------------------------------------------------------
 * Imports - Internal
 * ----------------------------------------------------------------------------------
 */
import * as Dtos from '../api/dtos';
import { Institution, PatientStateType } from '../api/dtos';
import { AppSettings } from '../AppSettings';
import SaConsentDialog from '../components/dialogs/consent/SaConsentDialog';
import ScreeningForm from '../components/forms/crf/ScreeningForm';
import ConsentFields, { consentFieldsLabels, consentFieldsLookups, useConsentFieldsValidation } from '../components/forms/fields/ConsentFields';
import ConsentDialogContext, { ConsentDialogState } from '../contexts/ConsentDialogContext';
import ScreeningDialogContext from '../contexts/ScreeningContext';
import useRadiologistPatients from '../hooks/data/useRadiologistPatients';
import useAllocatedColumns from '../hooks/useAllocatedColumns';
import useConsentColumns, { CONSENT_EVENT_DEFINITION_ID } from '../hooks/useConsentColumns';
import useFailedScreeningColumns from '../hooks/useFailedScreeningColumn';
import useIneligiblePatientColumns from '../hooks/useIneligiblePatientColumns';
import useRadiologistColumns from '../hooks/useRadiologistColumns';
import useRegistrationColumns from '../hooks/useRegistrationColumns';
import useScreeningColumns from '../hooks/useScreeningColumns';

/*
 * ----------------------------------------------------------------------------------
 * Interface
 * ----------------------------------------------------------------------------------
 */

interface IRegistrationProps {
    isBct: boolean;
}

interface IPatientTableProps {
    title: string;
    entityName: string;
    columns: Array<GridColDef<Dtos.Patient>>;
    onRowClick?: GridEventListener<'rowClick'>;
    patientStates?: Array<number>;
    startCollapsed?: boolean;
    patientsToUse?: Array<Dtos.Patient>;
}

interface IScreeningTableProps {
}

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */

//const MemoizedCollapsibleTable = memo(CollapsibleTable);


const SearchBox = styled(Box)(() => ({
    display: 'flex', 
    gap: '1rem', 
    flexWrap: 'wrap', 
    justifyContent: 'space-between',
    alignItems: 'end'
}));

const SearchTextField = styled(TextField)(({theme}) => ({
    width: '62%',

    [theme.breakpoints.down('md')]: {
        width: '100%'
    }
}));

const SearchButtonBox = styled(Box)(({theme}) => ({
    width: '35%',

    [theme.breakpoints.down('md')]: {
        width: '100%'
    }
}));

const PaperContainer = styled(Paper)(({theme}) => ({
    padding: theme.spacing(3)
}));

const AddButton = styled(Button)(({theme}) => ({
    width: '35%',

    [theme.breakpoints.down('md')]: {
        width: '100%'
    }
}));


const StyledButtonGroup = styled(Box)(({ theme }) => ({
    padding: theme.spacing(2, 2, 2, 2),
    textAlign: 'center',
    gap: theme.spacing(2),
}))

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
    padding: theme.spacing(2),

    '&>p': {
        padding: theme.spacing(0),
        fontSize: '1.25rem',
        fontWeight: 500
    }
}))

const StyledDialogContent = styled(DialogContent)(({theme}) => ({
    padding: theme.spacing(0, 0),
    overflow: 'visible',

    '&>p': {
        padding: `${theme.spacing(0, 2, 2, 2)} !important`,
    }
}));

/*
 * ----------------------------------------------------------------------------------
 * Components
 * ----------------------------------------------------------------------------------
 */
const screeningStates = [
    PatientStateType.Screening
]

const screeningFailedStates = [
    PatientStateType.ScreenFail
]

const consentStates = [
    PatientStateType.ConsentPending,
    PatientStateType.Consent
]

const registrationStates = [
    PatientStateType.Registration, 
    PatientStateType.RadiologistReview, 
    PatientStateType.Allocation
];

const allocatedStates = [
    PatientStateType.BctReview, 
    PatientStateType.CorrectionRequired, 
    PatientStateType.Allocated, 
    PatientStateType.PostAllocated, 
    PatientStateType.ThreeMonthsPostSurgery, 
    PatientStateType.SixMonthsPostSurgery, 
    PatientStateType.TwelveMonthsPostSurgery, 
    PatientStateType.TwentyFourMonthsPostSurgery, 
    PatientStateType.FiveYearsPostSurgery
];

const ineligibleStates = [
    PatientStateType.Ineligible
];

const allStates = [
    ...screeningStates,
    ...screeningFailedStates,
    ...consentStates,
    ...registrationStates,
    ...allocatedStates,
    ...ineligibleStates
]





const pageSizeOptions = [5, 10, 20, 50, 100]

const PatientTable: FC<IPatientTableProps> = ({
    title,
    entityName,
    columns,
    onRowClick,
    patientStates,
    startCollapsed,
    patientsToUse: patients
}) => {
    const [tableCount, setTableCount] = useState(1);

    const { data: contextPatients, loading: contextPatientsLoading } = useContext(PatientsContext);

    const patientsLoadingToUse = contextPatientsLoading;

    const patientsToUse = useMemo(() => {
        if(patients != null) {
            return patients;
        }

        return contextPatients?.filter(patient => (patientStates ?? []).length === 0 || patientStates?.includes(patient.patientStateId!))
        
    }, [contextPatients, patientStates, patients])

    useEffect(() => {
        setTableCount(x => x + 1);
    }, [contextPatients, columns, patients]);

    return (
        <>
            <CollapsibleTable
                key={tableCount}
                rows={patientsToUse ?? []}
                loading={patientsLoadingToUse}
                columns={columns as any}
                onRowClick={onRowClick}
                //options={tableOptions}
                pageSizeOptions={pageSizeOptions}
                initialState={{ pagination: { paginationModel: { pageSize: 10 } }}}
                title={title ?? "Participants"}
                pluralizeTitle={false}
                entityName={entityName ?? "Participant"}
                disabledTableText={"There are no participants"}
                startCollapsed={startCollapsed}

            />
        </>
        
    );
}

const Search: FC = () => {
    const navigate = useNavigate();

    const { data: patients } = useContext(PatientsContext);

    const [searchValue, setSearchValue] = useState<string|null>(null);

    const { enqueueSnackbar } = useSnackbar();

    const saveText: ChangeEventHandler<HTMLTextAreaElement | HTMLInputElement> = useCallback((e) => {
        if (!e.target.value || e.target.value === '') {
            setSearchValue(null);
        }
        else {
            setSearchValue(e.target.value);
        }
    }, [searchValue, setSearchValue]);

    const search = useCallback(() => {
        if (patients?.some(p => p.studyNumber === searchValue)) {
            navigate(`/registration/all-institutions/` + searchValue);
        }
        else {
            enqueueSnackbar(
                <>
                    <AlertTitle>
                        Participant Not Found
                    </AlertTitle>
                    {searchValue ? <>Participant '{searchValue}' not found. Please ensure you have correctly input the participant's study number.</> : <>The search field is empty.</>}
                </>,
                { variant: 'error' }
            );
        }
    }, [searchValue, navigate, patients]);

    return (
        <SearchBox>
            <SearchTextField
                variant="standard"
                label=" Participant Study Number"
                id='patientNumberSearch'
                onChange={saveText}
            />
            <SearchButtonBox>
                <Button
                    variant='contained'
                    onClick={search}
                    fullWidth
                >
                    Search
                </Button>
            </SearchButtonBox>
        </SearchBox>
    )
}

const ScreeningTable: FC<IScreeningTableProps> = () => {
    const { institutionCode } = useParams();

    const { data: institutions } = useContext(InstitutionsContext);
    const { setPatientStudyNumber, setDialogOpen, setScreeningOutcome } = useContext(ScreeningDialogContext);

    const parsedInstitutionCode = useMemo(() => {
        return institutionCode !== ALL_INSTITUTIONS_CODE ? institutionCode : null;
    }, [institutionCode]);

    const showInstitutions = useMemo(() => {
        return !parsedInstitutionCode
    }, [parsedInstitutionCode]);

    const onScreeningRowClick: GridEventListener<'rowClick'> = useCallback((params) => {
        setPatientStudyNumber(params.row?.studyNumber ?? null);
        setScreeningOutcome(null);
        setDialogOpen(true);
    }, [setPatientStudyNumber, setDialogOpen, setScreeningOutcome]);

    const institution = useMemo(() => {
        return institutions?.find(institution => institution.code === parsedInstitutionCode);
    }, [parsedInstitutionCode, institutions]);
    
    const {
        permissions: [canAdministerOpms],
    } = usePermissionsByIds(
        [
            Permission.OpmsDataManage, // BCT staff + CRA/CSM
        ], 
        undefined, 
        undefined, 
        institution?.id
    );

    const screeningColumns = useScreeningColumns(institutions ?? null, !canAdministerOpms, showInstitutions);

    return <>
        <PatientTable
            title="Screening"
            entityName="Participant"
            onRowClick={onScreeningRowClick}
            columns={screeningColumns}
            patientStates={screeningStates}
        />
    </>
}

const ConsentTable: FC<IScreeningTableProps> = () => {
    const navigate = useNavigate();

    const { institutionCode } = useParams();

    const parsedInstitutionCode = useMemo(() => {
        return institutionCode !== ALL_INSTITUTIONS_CODE ? institutionCode : null;
    }, [institutionCode]);

    const { data: institutions } = useContext(InstitutionsContext);

    const showInstitutions = useMemo(() => {
        return !parsedInstitutionCode
    }, [parsedInstitutionCode]);

    const onRowClick: GridEventListener<'rowClick'> = useCallback((params) => {
        navigate(`/registration/${institutionCode ?? ALL_INSTITUTIONS_CODE}/${params.row?.studyNumber}`)
    }, [history, institutionCode]);

    const institution = useMemo(() => {
        return institutions?.find(institution => institution.code === parsedInstitutionCode);
    }, [parsedInstitutionCode, institutions]);

    const {
        permissions: [canOpmsAdminister, canConsentAdminister],
    } = usePermissionsByIds(
        [
            Permission.OpmsDataManage, // BCT staff + CRA/CSM
            Permission.EConsentAdminister, // BCT staff + CRA/CSM
        ], 
        undefined, 
        undefined, 
        institution?.id
    );

    const consentColumns = useConsentColumns(institutions, showInstitutions, !canOpmsAdminister, canConsentAdminister);

    return <>
        <PatientTable
            title="Consent"
            entityName="Participant"
            onRowClick={onRowClick}
            columns={consentColumns}
            patientStates={[Dtos.PatientStateType.ConsentPending, Dtos.PatientStateType.Consent]}
        />
    </>
}

const ConsentDialog: FC = () => {
    const navigate = useNavigate();
    
    const { serviceStackClient: client } = useContext(OnlinePatientManagementContext);
    const { enqueueSnackbar } = useSnackbar();

    const { dialogState, setDialogState, patientStudyNumber, setPatientStudyNumber } = useContext(ConsentDialogContext);
    const { data: patients, refresh: patientRefresh } = useContext(PatientsContext);
    const { data: institutions} = useContext(InstitutionsContext);

    const patient = useMemo(() => {
        return patients?.find(x => x.studyNumber === patientStudyNumber)
    }, [patients, patientStudyNumber]);

    const consentEvent = useMemo(() => {
        return (patient as Dtos.Patient)?.consentEvents?.find(x => x.eventDefinitionId === CONSENT_EVENT_DEFINITION_ID)
    }, [patient]);
    
    const institution = useMemo(() => {
        return institutions?.find(x => x.id === patient?.institutionId)
    }, [institutions, patient]);

    const formMetadata = useFormMetadata('screening-form');

    const {
        data: screeningFormlookups
    } = useLookups(formMetadata);

    const formScreeningFormlookups = useFormLookups(screeningFormlookups);

    const { data: screeningForm, loading: screeningFormLoading } = useFormByCodes(formMetadata, institution?.code, patient?.studyNumber, 'screening', 1, 1, false, false, false);

    const closeDialog = useCallback(() => {
        setDialogState(ConsentDialogState.None);
        setPatientStudyNumber(null);
    }, [setDialogState, setPatientStudyNumber]);


    const consentInitialValues = useMemo(() => {
        return {
            consentEvent: {
                patientId: patient?.id,
            },
            eventDefinitionId: CONSENT_EVENT_DEFINITION_ID
        } as Dtos.StartConsent

    }, [patient]);

    const consentValidate = useConsentFieldsValidation(institution);

    const consentRefuseValidate: IFormValidate<Dtos.ScreeningForm, IFormValidationError> = useCallback(async (formState) => {

        let errors: Record<string, IFormValidationError[]> = {};

        if (formState.value?.screenFailReason === undefined) {
            errors['screenFailReason'] = [
                {
                    property: 'screenFailReason',
                    code: 'SF-001',
                    message: 'This field is required.',
                    detailedMessage: 'This field is required.',
                    type: ValidationErrorType.Normal
                }
            ];
        }

        return errors;
    }, [institution]);

    const [consentSaving, setConsentSaving] = useState(false); 
    const [_sendingEmail, setSendingEmail] = useState(false); 

    const onConsentSubmit: IFormSubmit<Dtos.StartConsent, IFormValidationError> = useCallback(async (form) => {
        
        setConsentSaving(true);

        try {
            await client.post(new Dtos.StartConsent({...form.value }))

            closeDialog();

            if(form.value?.consentEvent.consentTypeId === Dtos.ConsentTypeEnum.Standard)
            {
                navigate(`/registration/${ALL_INSTITUTIONS_CODE}/${patient?.studyNumber}`)
            }
            else
            {
                await patientRefresh();
            }

            setConsentSaving(false);

            enqueueSnackbar(
                <>
                    <AlertTitle>
                        Consent Started
                    </AlertTitle>
                    Consent started successfully.
                </>,
                { variant: 'success' }
            );
        }
        catch (error: any) {
            setConsentSaving(false);

            enqueueSnackbar(
                <>
                    <AlertTitle>
                        Consent Not Started
                    </AlertTitle>
                    An error occurred while attempting to start the consent.
                </>,
                { variant: 'critical' }
            );

            enqueueSnackbar(
                <>
                    <AlertTitle>
                        Consent Not Started
                    </AlertTitle>
                    {error?.responseStatus?.errorCode === 'ClientViewableException' ? error?.responseStatus?.message : 'An error occurred while attempting to start the consent.'}
                </>,
                { variant: 'critical' }
            );
        }
    
    }, [client, enqueueSnackbar, closeDialog, setConsentSaving, patientRefresh, history, patient]);

    const onNotifyClick = useCallback(async () => {
        setSendingEmail(true);

        try {
            await client.post(new Dtos.SendConsentInformation({ 
                patientId: patient?.id,
                eventDefinitionId: CONSENT_EVENT_DEFINITION_ID 
            }));

            enqueueSnackbar(
                <>
                    <AlertTitle>
                        Consent Notification Sent
                    </AlertTitle>
                    The consent notification was successfully sent.
                </>,
                { variant: 'success' }
            );

            closeDialog();
            setSendingEmail(false);
        }
        catch {
            enqueueSnackbar(
                <>
                    <AlertTitle>
                    Consent Notification Not Sent
                    </AlertTitle>
                    An error occurred while attempting to send the consent notification.
                </>,
                { variant: 'critical' }
            );

            closeDialog();
            setSendingEmail(false);
        }
    }, [patient, enqueueSnackbar, client, setSendingEmail, closeDialog]);

    const { cache } = useSWRConfig();

    const onRefuseSubmit: IFormSubmit<Dtos.ScreeningForm, IFormValidationError> = useCallback(async (form) => {
        
        setConsentSaving(true);

        try {
            await client.post(new Dtos.ScreeningFormPostSave({
                form: new Dtos.ScreeningForm({ 
                    ...form.value, 
                    outcome: Dtos.LookupScreeningOutcomeType.Fail 
                })
            }));

            closeDialog();

            patientRefresh();

            for (let key of Array.from(cache.keys()).filter(x => x.startsWith('SwrClient:ScreeningFormGetSingleByIds'))) {
                cache.delete(key);
            }

            setConsentSaving(false);

            enqueueSnackbar(
                <>
                    <AlertTitle>
                        Partipant Marked As Screen Fail
                    </AlertTitle>
                    Participant was marked as screen fail successfully.
                </>,
                { variant: 'success' }
            );
        }
        catch (error: any) {
            setConsentSaving(false);

            enqueueSnackbar(
                <>
                    <AlertTitle>
                        Partipant Not Marked As Screen Fail
                    </AlertTitle>
                    {error?.responseStatus?.errorCode === 'ClientViewableException' ? error?.responseStatus.message : 'An error occurred while attempting to mark the participant as screen fail.'}
                </>,
                { variant: 'critical' }
            );
        }    
    }, [client, enqueueSnackbar, closeDialog, setConsentSaving, patientRefresh, patient, cache]);

    return <>
        <Dialog
            open={dialogState === ConsentDialogState.Restart}
            onClose={closeDialog}
            aria-labelledby="restart-dialog-title"
            aria-describedby="restart-dialog-description"
            maxWidth="md"
            fullWidth
        >
            <StyledDialogTitle id="restart-dialog-title">
                <Typography
                    color="secondary"
                >
                    Restart Consent (Participant {patientStudyNumber})
                </Typography>
            </StyledDialogTitle>
            <StyledDialogContent>
                <DialogContentText id="resend-dialog-description">
                    Consent started on <strong>{consentEvent?.dateStarted ? DateTime.fromISO(consentEvent?.dateStarted).toFormat('dd MMM yyyy') : ''}</strong>. Restarting the consent will remove any data in the existing consent forms.
                </DialogContentText>
                <DialogContentText id="create-dialog-description">
                    Consent forms will be using the latest PICF version available by default.
                </DialogContentText>

                <Form
                    initialValue={consentInitialValues}
                    onSubmit={onConsentSubmit}    
                    lookups={consentFieldsLookups}
                    labels={consentFieldsLabels}
                    validateOn={ValidateOn.onChange}  
                    onValidate={consentValidate}
                    debug={AppSettings.debug}
                >
                    <ConsentFields institutionCode={(patient as Dtos.Patient)?.institutionCode} />

                    <StyledButtonGroup
                        display="flex"
                        justifyContent="flex-end"
                    >
                        <Button
                            color="secondary"
                            onClick={closeDialog}
                            loading={consentSaving}  
                        >
                            Cancel
                        </Button>

                        <FormSubmitButton
                            color="primary"   
                            variant="contained"     
                            loading={consentSaving}                                    
                        >
                            Restart
                        </FormSubmitButton>
                    </StyledButtonGroup>
                </Form>
            </StyledDialogContent>
        </Dialog>
        <Dialog
            open={dialogState === ConsentDialogState.Start}
            onClose={closeDialog}
            aria-labelledby="start-dialog-title"
            aria-describedby="start-dialog-description"
            maxWidth="md"
            fullWidth
        >
            <StyledDialogTitle id="start-dialog-title">
                <Typography
                    color="secondary"
                >
                    Start Consent (Participant {patientStudyNumber})
                </Typography>
            </StyledDialogTitle>
            <StyledDialogContent>
                <DialogContentText id="create-dialog-description">
                    Please provide the following information to start the consent. 
                </DialogContentText>
                <DialogContentText id="create-dialog-description">
                    Consent forms will be using the latest PICF version available by default.
                </DialogContentText>

                <Form
                    initialValue={consentInitialValues}
                    onSubmit={onConsentSubmit}   
                    lookups={consentFieldsLookups}
                    labels={consentFieldsLabels}
                    validateOn={ValidateOn.onChange} 
                    onValidate={consentValidate}    
                    debug={AppSettings.debug}
                >
                    <ConsentFields institutionCode={(patient as Dtos.Patient)?.institutionCode} />

                    <StyledButtonGroup
                        display="flex"
                        justifyContent="flex-end"
                    >
                        <Button
                            color="secondary"
                            onClick={closeDialog}
                            loading={consentSaving}  
                        >
                            Cancel
                        </Button>

                        <FormSubmitButton
                            color="primary"   
                            variant="contained"     
                            loading={consentSaving}                                    
                        >
                            Start
                        </FormSubmitButton>

                    </StyledButtonGroup>
                </Form>
            </StyledDialogContent>
        </Dialog>
        <Dialog
            open={dialogState === ConsentDialogState.Notify}
            onClose={closeDialog}
            aria-labelledby="notify-dialog-title"
            aria-describedby="notify-dialog-description"
        >
            <StyledDialogTitle id="notify-dialog-title">
                <Typography
                    color="secondary"
                >
                    Send Notifification (Participant {patientStudyNumber})
                </Typography>
            </StyledDialogTitle>
            <StyledDialogContent>
                <DialogContentText id="create-dialog-description">
                    Sending notification will send an email notification to the patient or investigator.
                </DialogContentText>
                <DialogContentText id="create-dialog-description">
                    Are you sure you wish to continue?
                </DialogContentText>
            </StyledDialogContent>
            <DialogActions style={{paddingLeft: 0, paddingRight: 0}}>
                <StyledButtonGroup
                    display="flex"
                    justifyContent="flex-end"
                >
                    <Button onClick={closeDialog} color="secondary">
                        No
                    </Button>
                    <Button onClick={onNotifyClick} color="primary" variant="contained" autoFocus>
                        Yes
                    </Button>
                </StyledButtonGroup>
            </DialogActions>
        </Dialog>
        <Dialog
            open={dialogState === ConsentDialogState.Refuse}
            onClose={closeDialog}
            aria-labelledby="refuse-dialog-title"
            aria-describedby="refuse-dialog-description"
            maxWidth="sm"
            fullWidth
        >
            <StyledDialogTitle id="refuse-dialog-title">
                <Typography
                    color="secondary"
                >
                    Refuse Consent (Participant {patientStudyNumber})
                </Typography>
            </StyledDialogTitle>
            <StyledDialogContent>
                <DialogContentText id="create-dialog-description">
                    The participant will be marked as screen fail.
                </DialogContentText>

                <Form
                    initialValue={screeningForm}
                    onSubmit={onRefuseSubmit}   
                    lookups={formScreeningFormlookups}
                    validateOn={ValidateOn.onChange} 
                    onValidate={consentRefuseValidate}
                    debug={AppSettings.debug}
                >
                    <GroupedField
                        propertyPath="screenFailReason"
                        LabelProps={{
                            label: "Please provide the details below" as any
                        }}
                        component={Select}
                        FieldGridProps={{
                            size: {
                                xs: 12,
                                sm: 12,
                                md: 12,
                                lg: 12,
                                xl: 12
                            }
                        }}
                    />

                    <StyledButtonGroup
                        display="flex"
                        justifyContent="flex-end"
                    >
                        <Button
                            color="secondary"
                            onClick={closeDialog}
                            loading={screeningFormLoading || consentSaving}  
                        >
                            Cancel
                        </Button>

                        <FormSubmitButton
                            color="primary"   
                            variant="contained"     
                            loading={screeningFormLoading || consentSaving}                                    
                        >
                            Mark as Screen Fail
                        </FormSubmitButton>
                    </StyledButtonGroup>
                </Form>
            </StyledDialogContent>
        </Dialog>
    </>
}

const Registration: FC<IRegistrationProps> = ({
    isBct
}) => {
    const navigate = useNavigate();

    const { masterGroupCode, collaboratingGroupCode, countryCode, institutionCode } = useParams();

    const parsedInstitutionCode = useMemo(() => {
        return institutionCode !== ALL_INSTITUTIONS_CODE ? institutionCode : undefined;
    }, [institutionCode]);

    const { data: institutions } = useContext(InstitutionsContext);

    const institution = useMemo(() => {
        return institutions?.find(institution => institution.code === parsedInstitutionCode);
    }, [parsedInstitutionCode, institutions]);

    const showInstitutions = useMemo(() => {
        return !parsedInstitutionCode
    }, [parsedInstitutionCode]);

    const institutionsToUse = useMemo(() => {
        return institutions?.filter(i => i.code);
    }, [institutions]);

    const {
        permissions: [canAdministerOpms, canPatientUpdate, canAdministerRadiology, canCrfUpdate],
        loading: permissionsLoading
    } = usePermissionsByIds(
        [
            Permission.OpmsDataManage, // BCT staff + CRA/CSM
            Permission.OpmsPatientUpdate,
            Permission.RadiologyReviewAccess,
            Permission.EcrfView,
            Permission.EConsentAdminister
        ], 
        undefined, 
        undefined, 
        institution?.id
    );

    const isRadiologist = canAdministerRadiology && !canCrfUpdate;

    const { data: user } = useContext(AuthenticatedUserContext);

    const { data: treatments } = useContext(TreatmentsContext);

    const { data: radiologistPatients } = useRadiologistPatients(isRadiologist ? user?.userName : undefined);

    const [saPatient, setSaPatient] = useState<Dtos.Patient | undefined>(undefined);

    const handleSaConsentClick = useCallback((patient: Dtos.Patient) => {
        setSaPatient(patient)
    }, [setSaPatient]);

    const handleCancelClick = useCallback(() => {
        setSaPatient(undefined);
    }, [setSaPatient]);

    const handleReviewClick = useCallback(() => {
        setSaPatient(undefined);
    }, [setSaPatient]);

    const failedScreeningColumns = useFailedScreeningColumns(institutions ?? null, !canAdministerOpms, showInstitutions);
    const registrationColumns = useRegistrationColumns(institutions ?? null, !canAdministerOpms, showInstitutions, handleSaConsentClick, isBct);
    const radiologistReviewColumns = useRadiologistColumns(institutions ?? null, !canAdministerOpms, showInstitutions, false);
    const radiologistReviewedColumns = useRadiologistColumns(institutions ?? null, !canAdministerOpms, showInstitutions, true);
    //const allocationColumns = useAllocationColumns(institutions, showInstitutions);
    const allocatedColumns = useAllocatedColumns(institutions ?? null, !canAdministerOpms, showInstitutions, treatments, handleSaConsentClick, isBct);
    const ineligibleColumns = useIneligiblePatientColumns(institutions ?? null, !canAdministerOpms, showInstitutions);

    const [screeningDialogOpen, setScreeningDialogOpen] = useState(false);
    const [patientStudyNumber, setPatientStudyNumber] = useState<string|null>(null);
    const [screeningOutcome, setScreeningOutcome] = useState<Dtos.LookupScreeningOutcomeType|null>(null);

    const [consentDialogState, setConsentDialogState] = useState(ConsentDialogState.None);

    const screeningDialogContext = useMemo(() => {
        return {
            dialogOpen: screeningDialogOpen,
            setDialogOpen: setScreeningDialogOpen,
            patientStudyNumber,
            setPatientStudyNumber,
            institutionCode: parsedInstitutionCode,
            screeningOutcome,
            setScreeningOutcome
        }
    }, [screeningDialogOpen, setScreeningDialogOpen, patientStudyNumber, setPatientStudyNumber, parsedInstitutionCode, screeningOutcome, setScreeningOutcome]);

    
    const consentDialogContext = useMemo(() => {
        return {
            dialogState: consentDialogState,
            setDialogState: setConsentDialogState,
            patientStudyNumber,
            setPatientStudyNumber,
            institutionCode: parsedInstitutionCode
        }
    }, [consentDialogState, setConsentDialogState, patientStudyNumber, setPatientStudyNumber, parsedInstitutionCode]);

    //@ts-expect-error
    const onSelect = useCallback((newMasterGroupCode?: string | null, newCollaboratingGroupCode?: string | null, newCountryCode?: string | null, newInstitutionCode?: string | null) => {
        if (newInstitutionCode) {
            navigate(`/registration/${newInstitutionCode}`);
        }
        else {
            navigate(`/registration`);
        }

        return;
    }, [navigate]);

    const onRowClick: GridEventListener<'rowClick'> = useCallback((params) => {
        if (isRadiologist) {
            navigate(`/registration/${params.row?.institutionCode}/${params.row?.studyNumber}/radiologist-review/1/radiology-report/1`)
        } else {
            navigate(`/registration/${institutionCode ?? ALL_INSTITUTIONS_CODE}/${params.row?.studyNumber}`)
        }
    }, [history, institutionCode, canAdministerRadiology]);

    const reviewPatients = radiologistPatients?.filter(x => x.patientStateId === PatientStateType.RadiologistReview);
    const reviewedPatients = radiologistPatients?.filter(x => (x.patientStateId ?? 0) > PatientStateType.RadiologistReview && (x.patientStateId ?? 0) < PatientStateType.ScreenFail );

    const onScreeningRowClick: GridEventListener<'rowClick'> = useCallback((params) => {
        setPatientStudyNumber(params.row?.studyNumber ?? null);
        setScreeningOutcome(null);
        setScreeningDialogOpen(true);
    }, [setPatientStudyNumber, setScreeningDialogOpen, setScreeningOutcome]);

    if (permissionsLoading) {
        return (
            <RouteLoading />
        );
    }

    return (
        <PageLayout
            breadcrumbs={<InstitutionBreadcrumbs />}
            heading="Participants"
        >
            <PaperContainer>
                <PatientsByCodesProvider
                    
                    institutionCode={parsedInstitutionCode}
                    patientStateIds={allStates}
                >
                    <TrialContextSelector
                        onChange={onSelect}
                        allowAllMasterGroups={true}
                        allowAllCollaboratingGroups={true}
                        allowAllCountries={true}
                        allowAllInstitutions={true}
                        hideMasterGroups={true}
                        hideCollaboratingGroups={true}
                        hideCountries={true}
                        masterGroupCode={masterGroupCode}
                        collaboratingGroupCode={collaboratingGroupCode}
                        countryCode={countryCode}
                        institutionCode={institutionCode}
                        institutions={institutionsToUse}
                    >
                        {
                            <Grid2
                                size={{xs: 12, sm: 6}}
                            >
                                <Search />
                            </Grid2>
                        }  
                        <Grid2
                            size={{xs: 12, sm: 6}}
                            style={{ padding: 0}}
                        >
                        </Grid2>
                        {
                            !!canPatientUpdate && <Grid2
                                size={{xs: 12, sm: 6}}
                                style={{display: 'flex', justifyContent: 'end', paddingTop: 0}}
                            >
                                <AddButton
                                    variant="contained"
                                    color="primary"
                                    //component={(Link)}
                                    //to={`/registration/${parsedInstitutionCode}/add-participant`}
                                    fullWidth
                                    disabled={!institution || (institution as Institution).recruitmentDisabled}
                                    onClick={() => setScreeningDialogOpen(true)}
                                >
                                    Add Participant
                                </AddButton>
                            </Grid2>
                        }
                    </TrialContextSelector>

                    {
                        !isRadiologist && <ScreeningDialogContext.Provider value={screeningDialogContext}>
                            <ScreeningTable />
                            <ScreeningForm />
                        </ScreeningDialogContext.Provider>
                    }

                    {
                        !isRadiologist && <ConsentDialogContext.Provider value={consentDialogContext}>
                            <ConsentTable />
                            <ConsentDialog />
                        </ConsentDialogContext.Provider>
                    }

                    {
                        !isRadiologist && <PatientTable
                            title={"Registration"}
                            entityName="Participant"
                            onRowClick={onRowClick}
                            columns={registrationColumns}
                            patientStates={registrationStates}
                        />
                    }
                    {
                        !!isRadiologist && <>
                            <PatientTable
                                title="Radiologist Review"
                                entityName="Participant"
                                onRowClick={onRowClick}
                                columns={radiologistReviewColumns}
                                patientsToUse={reviewPatients}
                                patientStates={registrationStates}
                            />
                            <PatientTable
                                title="Reviewed"
                                entityName="Participant"
                                onRowClick={onRowClick}
                                columns={radiologistReviewedColumns}
                                patientsToUse={reviewedPatients}
                                patientStates={registrationStates}
                                startCollapsed={true}
                            />
                        </>
                    }

                    {
                        !isRadiologist && <PatientTable
                            title="Allocated"
                            entityName="Participant"
                            onRowClick={onRowClick}
                            columns={allocatedColumns}
                            patientStates={allocatedStates}
                            startCollapsed={true}
                        />
                    }

                    {
                        !isRadiologist && <ScreeningDialogContext.Provider value={screeningDialogContext}>
                            <PatientTable
                                title="Failed Screenings"
                                entityName="Participant"
                                onRowClick={onScreeningRowClick}
                                columns={failedScreeningColumns}
                                patientStates={screeningFailedStates}
                                startCollapsed={true}
                            />
                        </ScreeningDialogContext.Provider>
                    }

                    {
                        !isRadiologist && (
                            <PatientTable
                                title="Ineligible Participants"
                                entityName="Participant"
                                onRowClick={onRowClick}
                                columns={ineligibleColumns}
                                patientStates={ineligibleStates}
                                startCollapsed={true}
                            />
                        )
                    }

                    <CrfFormByCodesProvider
                        createForm
                        createEvent
                        patientStudyNumber={saPatient?.studyNumber}
                        eventDefinitionIdentifier={"consent"}
                        eventRepeat={1}
                        formDefinitionIdentifier={"services-australia-consent"}
                        formRepeat={1}
                        institutionCode={saPatient?.institutionCode}
                        loadBeforeMount
                    >
                        <SaConsentDialog
                            patient={saPatient}
                            onCancel={handleCancelClick}
                            onReview={handleReviewClick}
                            open={!!saPatient}
                        />

                        {/* <TissueConsentDialog 
                            patient={tissuePatient}
                            open={!!tissuePatient}
                        /> */}
                    </CrfFormByCodesProvider>
                </PatientsByCodesProvider>
            </PaperContainer>
        </PageLayout>
    );
}


/*
 * ----------------------------------------------------------------------------------
 * Default Export
 * ----------------------------------------------------------------------------------
 */

export default Registration;
